/**
 * "This page is also in English / español" banner, as pure functions (src/site/langBanner.ts
 * is the browser side). Never redirects: `/` stays Spanish for crawlers and for people who
 * chose it; the banner only points a visitor whose browser prefers the other language at
 * the twin page (from the page's own hreflang alternates) or, without a twin, at the other
 * home. Dismissing it is remembered per browser.
 */
import { isLang, pagePath, type Lang } from './seo.js';

/** Browser languages that read the Spanish site: Spanish plus Spain's co-official languages. */
const SPANISH_READERS = new Set(['es', 'ca', 'gl', 'eu']);

export const LANG_BANNER_DISMISSED_KEY = 'rt.langBanner.dismissed';

export interface LangSuggestion {
  lang: Lang;
  href: string;
  /** True when href is this page's twin; false when it's the other language's home. */
  twin: boolean;
}

/** The site language a browser's preference list maps to: its FIRST entry decides
 *  (Spain's co-official languages read Spanish; a language we don't have reads English). */
export function preferredSiteLang(browserLangs: readonly string[]): Lang | undefined {
  const first = browserLangs[0]?.trim().toLowerCase();
  if (!first) return undefined;
  const primary = first.split(/[-_]/)[0] ?? '';
  if (SPANISH_READERS.has(primary)) return 'es';
  return isLang(primary) ? primary : 'en';
}

/**
 * What to suggest, or undefined when the visitor already reads the page's language.
 * `alternates` are the page's own hreflang links (lang → absolute or root-relative URL).
 */
export function suggestLanguage(
  pageLang: Lang,
  browserLangs: readonly string[],
  alternates: Partial<Record<string, string>>,
): LangSuggestion | undefined {
  const want = preferredSiteLang(browserLangs);
  if (!want || want === pageLang) return undefined;
  const twin = alternates[want];
  if (twin) return { lang: want, href: twin, twin: true };
  return { lang: want, href: pagePath('home', want), twin: false };
}

const BANNER: Record<Lang, { twin: [string, string]; home: [string, string]; close: string }> = {
  es: { twin: ['Esta página también está en español.', 'Leer en español'], home: ['RoomTour también está en español.', 'Ir a la web en español'], close: 'Cerrar' },
  en: { twin: ['This page is also available in English.', 'Read it in English'], home: ['RoomTour is also available in English.', 'Go to the English site'], close: 'Dismiss' },
  fr: { twin: ['Cette page existe aussi en français.', 'Lire en français'], home: ['RoomTour existe aussi en français.', 'Aller au site en français'], close: 'Fermer' },
  de: { twin: ['Diese Seite gibt es auch auf Deutsch.', 'Auf Deutsch lesen'], home: ['RoomTour gibt es auch auf Deutsch.', 'Zur deutschen Website'], close: 'Schließen' },
  it: { twin: ['Questa pagina è disponibile anche in italiano.', 'Leggi in italiano'], home: ['RoomTour è disponibile anche in italiano.', 'Vai al sito in italiano'], close: 'Chiudi' },
  pt: { twin: ['Esta página também está em português.', 'Ler em português'], home: ['O RoomTour também está em português.', 'Ir para o site em português'], close: 'Fechar' },
};

/** Banner copy, written in the SUGGESTED language (the one the visitor reads). */
export function bannerCopy(s: LangSuggestion): { text: string; action: string; close: string } {
  const b = BANNER[s.lang];
  const [text, action] = s.twin ? b.twin : b.home;
  return { text, action, close: b.close };
}

/** Root-relative path of a same-site absolute URL, so the banner link works on any host. */
export function sitePath(href: string, origin: string): string {
  return href.startsWith(origin) ? href.slice(origin.length) || '/' : href;
}
